import { useState } from 'react';
import { useAsync } from 'react-async';
import { useDebounce } from 'react-use';
import { useTranslation } from 'react-i18next';
import { OrderColumnEnum, OrderDirectionEnum } from '@koob/enums';
import {
  KoobAvatar,
  KoobCard,
  KoobEmptyState,
  KoobInputSelect,
  KoobInputStars,
  KoobLoader,
  KoobPageHeader,
  KoobPlaceholder,
  KoobPopover,
  KoobSearchBar,
} from '@koob/ui';
import { getHotel, getHotels } from 'api/node/hotels';
import { getKinds } from 'api/node/kinds';
import { KoobLocationSearch, SEARCH_DEFAULT_TYPES } from '@koob/search';
import ActiveJobTooltip from 'components/ActiveJobTooltip';
import { Container } from 'ui';
import { decodeId } from 'utils';
import { useApp, useSearchParams } from 'hooks';
import { formatHotelResult } from './allotmentsUtils';

function HotelSummary({ hotel }) {
  return (
    <span className="flex min-w-0 items-center gap-3">
      <KoobAvatar src={hotel.image} name={hotel.name} size={12} rounded="lg" />
      <span className="min-w-0 text-left">
        <span className="block truncate font-semibold text-k-h">
          {hotel.name}
        </span>
        <KoobInputStars value={hotel.stars} size="xs" />
        {hotel.description && (
          <span className="block truncate text-xs text-navy-500">
            {hotel.description}
          </span>
        )}
      </span>
    </span>
  );
}

function HotelPicker({ hotel, onSelect }) {
  const { t, i18n } = useTranslation('allotments');
  const { currentOrganizationId } = useApp();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const [stars, setStars] = useState(null);
  const [kinds, setKinds] = useState([]);
  const [location, setLocation] = useState(null);
  useDebounce(() => setSearch(query), 300, [query]);
  const hasFilters = Boolean(stars || kinds.length || location);

  const { data: kindsData } = useAsync({ promiseFn: getKinds });
  const kindOptions = (kindsData?.data ?? []).map((kind) => ({
    label: kind.displayName,
    value: kind.id,
  }));

  const { data, isLoading } = useAsync({
    promiseFn: getHotels,
    skip: !open,
    filters: {
      direction: OrderDirectionEnum.DESC,
      order: OrderColumnEnum.UPDATED_AT,
      search,
      stars,
      kinds: kinds.length ? kinds : undefined,
      ...(location?.kind && { [location.kind]: [location.id] }),
      dmcId: decodeId(currentOrganizationId),
      discarded: false,
      perPage: 100,
      page: 1,
      includeActiveJobs: true,
    },
    locale: 'en',
    watch: JSON.stringify([open, search, stars, kinds, location]),
  });
  const hotels = (data?.data?.data ?? []).map(formatHotelResult);

  return (
    <KoobPopover
      open={open}
      onOpenChange={setOpen}
      flush
      trigger={
        <button
          type="button"
          className="flex h-10 w-full cursor-pointer items-center gap-2 rounded-[10px] border-[1.5px] border-solid border-navy-200 bg-white px-3 font-[inherit] text-sm text-k-h transition-colors hover:border-navy-300 focus:outline-none focus-visible:border-k-h focus-visible:ring-4 focus-visible:ring-navy-100 sm:w-80"
        >
          {hotel ? (
            <>
              <KoobAvatar
                src={hotel.image}
                name={hotel.name}
                size={6}
                rounded="md"
              />
              <span className="min-w-0 flex-1 truncate text-left font-medium">
                {hotel.name}
              </span>
            </>
          ) : (
            <span className="flex-1 text-left text-navy-400">
              <i className="fa-regular fa-hotel mr-2" />
              {t('hotelSelector.title')}
            </span>
          )}
          <i className="fa-regular fa-chevron-down text-xs text-navy-400" />
        </button>
      }
    >
      <div className="w-[min(32rem,calc(100vw-2rem))]">
        <div className="space-y-2 border-0 border-b border-solid border-k-gray p-3">
          <KoobSearchBar
            value={query}
            onChange={setQuery}
            placeholder={t('hotelSelector.searchPlaceholder')}
            autoFocus
          />
          <KoobLocationSearch
            value={location}
            onValueChange={setLocation}
            types={SEARCH_DEFAULT_TYPES}
            locale={i18n.language}
            pluralKinds
            placeholder={t('hotelSelector.locationPlaceholder')}
            icon={<i className="fa-regular fa-location-dot" aria-hidden />}
          />
          <div className="flex items-center gap-3">
            <div className="min-w-0 flex-1">
              <KoobInputSelect
                options={kindOptions}
                value={kinds}
                onValueChange={setKinds}
                placeholder={t('bookings:kindOfHotel')}
                multiple
                clearable
              />
            </div>
            <KoobInputStars value={stars} onValueChange={setStars} />
            {hasFilters && (
              <button
                type="button"
                className="shrink-0 cursor-pointer border-none bg-transparent p-0 font-[inherit] text-sm text-navy-500 hover:text-k-h"
                onClick={() => {
                  setStars(null);
                  setKinds([]);
                  setLocation(null);
                }}
              >
                {t('bookings:resetFilters')}
              </button>
            )}
          </div>
        </div>

        <div className="max-h-[22rem] overflow-y-auto p-1.5">
          {isLoading && hotels.length === 0 ? (
            <KoobPlaceholder className="m-1.5 h-14 rounded-lg" count={4} />
          ) : hotels.length === 0 ? (
            <KoobEmptyState
              variant="plain"
              description={t('hotelSelector.empty')}
            />
          ) : (
            hotels.map((item) => (
              <button
                key={item.id}
                type="button"
                disabled={item.hasActiveJob}
                onClick={() => {
                  onSelect(item);
                  setOpen(false);
                }}
                className={`flex w-full cursor-pointer items-center justify-between gap-3 rounded-lg border-none p-2 font-[inherit] transition-colors hover:bg-navy-50 disabled:cursor-not-allowed disabled:opacity-50 ${
                  item.id === hotel?.id ? 'bg-navy-50' : 'bg-transparent'
                }`}
              >
                <HotelSummary hotel={item} />
                {item.hasActiveJob ? (
                  <ActiveJobTooltip>
                    <KoobLoader size={16} color="currentColor" />
                  </ActiveJobTooltip>
                ) : (
                  item.id === hotel?.id && (
                    <i className="fa-regular fa-check text-k-orange" />
                  )
                )}
              </button>
            ))
          )}
        </div>
      </div>
    </KoobPopover>
  );
}

/**
 * A page that works on one hotel at a time (allotments, night rates): the
 * hotel picker and the editor in one card. The hotel lives in `?hotelId=`,
 * so links can open a page on a hotel.
 */
export default function HotelPickerPage({ title, kind, children }) {
  const { t } = useTranslation('allotments');
  const [searchParams, setSearchParams] = useSearchParams();
  const { hotelId } = searchParams;

  const { data } = useAsync({ promiseFn: getHotel, hotelId, watch: hotelId });
  const hotelData = hotelId ? data?.data : null;
  const hotel = hotelData ? formatHotelResult(hotelData) : null;

  return (
    <>
      <KoobPageHeader title={title} />

      <Container variant="main" size="full">
        <KoobCard>
          <HotelPicker
            hotel={hotel}
            onSelect={(item) =>
              setSearchParams({ ...searchParams, hotelId: item.id })
            }
          />

          <div className="mt-6 border-0 border-t border-solid border-k-gray pt-6">
            {hotel ? (
              children({ hotel, hotelData })
            ) : (
              <KoobEmptyState
                variant="plain"
                icon={<i className="fa-light fa-hotel" />}
                description={t('hotelSelector.description', {
                  kind: t(`hotelSelector.kinds.${kind}`),
                })}
              />
            )}
          </div>
        </KoobCard>
      </Container>
    </>
  );
}
